import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@env/environment';
import { RecaptchaService } from '@shared/services/recaptcha/recaptcha.service';
import { isRecaptchaRetryableError, RECAPTCHA_HEADER_NAME, resolveRecaptchaAction } from '@shared/utils/recaptcha.util';
import { catchError, defer, from, Observable, switchMap, throwError } from 'rxjs';

function appendRecaptchaHeader(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
    if (!token) {
        return request;
    }

    return request.clone({
        setHeaders: {
            [RECAPTCHA_HEADER_NAME]: token
        }
    });
}

function executeWithRecaptcha(
    request: HttpRequest<unknown>,
    action: string,
    next: HttpHandlerFn,
    recaptchaService: RecaptchaService
): Observable<HttpEvent<unknown>> {
    return defer(() => from(recaptchaService.getToken(action))).pipe(switchMap((token) => next(appendRecaptchaHeader(request, token))));
}

export const recaptchaInterceptor: HttpInterceptorFn = (request, next) => {
    const action = resolveRecaptchaAction(request, environment.shippingRecords.api);
    if (!action || request.headers.has(RECAPTCHA_HEADER_NAME)) {
        return next(request);
    }

    const recaptchaService = inject(RecaptchaService);

    return executeWithRecaptcha(request, action, next, recaptchaService).pipe(
        catchError((error) => {
            if (!isRecaptchaRetryableError(error)) {
                return throwError(() => error);
            }

            return executeWithRecaptcha(request, action, next, recaptchaService);
        })
    );
};
